import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AuthLayout from '../../components/layout/AuthLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import { Loader2, ShieldCheck } from 'lucide-react';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../../components/ui/input-otp';

const MFAVerifyPage = () => {
  const [token, setToken] = useState('');
  const [verifying, setVerifying] = useState(false);
  const { verifyMfa, tempUserId } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (!tempUserId) {
      navigate('/login');
    }
  }, [tempUserId, navigate]);

  const handleVerify = async () => {
    if (token.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    setVerifying(true);
    try {
      await verifyMfa(token);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
      setToken('');
    } finally {
      setVerifying(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && token.length === 6) {
      handleVerify();
    }
  };

  return (
    <AuthLayout>
      <Card className="border-0 shadow-xl">
        <CardHeader className="space-y-1 pb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-10 w-10 rounded-lg bg-indigo-100 flex items-center justify-center">
              <ShieldCheck className="h-5 w-5 text-indigo-600" />
            </div>
          </div>
          <CardTitle className="font-outfit text-2xl font-semibold text-slate-900">
            Two-Factor Authentication
          </CardTitle>
          <CardDescription className="text-slate-500">
            Enter the 6-digit code from your authenticator app
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex justify-center" onKeyDown={handleKeyDown}>
            <InputOTP
              maxLength={6}
              value={token}
              onChange={(value) => setToken(value)}
              data-testid="mfa-verify-otp-input"
            >
              <InputOTPGroup>
                <InputOTPSlot index={0} />
                <InputOTPSlot index={1} />
                <InputOTPSlot index={2} />
                <InputOTPSlot index={3} />
                <InputOTPSlot index={4} />
                <InputOTPSlot index={5} />
              </InputOTPGroup>
            </InputOTP>
          </div>

          <Button
            className="w-full h-11 bg-indigo-600 hover:bg-indigo-700 font-medium"
            onClick={handleVerify}
            disabled={verifying || token.length !== 6}
            data-testid="mfa-verify-submit-btn"
          >
            {verifying ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Verifying...
              </>
            ) : (
              'Verify'
            )}
          </Button>

          <Button
            variant="ghost"
            className="w-full text-slate-500 hover:text-slate-700"
            onClick={() => navigate('/login')}
            data-testid="mfa-back-to-login-btn"
          >
            Back to login
          </Button>
        </CardContent>
      </Card>
    </AuthLayout>
  );
};

export default MFAVerifyPage;
